import React, { Suspense, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ERROR_CODES } from "@components/common/error";
import { MobileGate } from "@components/common/mobile-gate";
import { PATHS } from "@constants/Paths";
import { AnalyticsArea, useBillingEnabled, usePageTracking, useTrackEvent } from "@hooks";
import { AppLayout, AuthLayout, BoxLayout, DataProviderLayout } from "@layouts";
import { PeekScreenLoader } from "@piiqtechnologies/ui-components";
import { useAuthContext } from "@stores/AuthContext";
/** Renders children when billing is enabled, otherwise redirects to /home. */
const BillingRoute = ({ children }) => {
    const billingEnabled = useBillingEnabled();
    if (!billingEnabled)
        return React.createElement(Navigate, { to: PATHS.HOME, replace: true });
    return children;
};
/**
 * Catch-all 404. A thin wrapper (not the shared Error component, which is also rendered as the
 * Sentry boundary fallback outside the analytics providers) so we can emit a Page Not Found event.
 */
const NotFound = () => {
    const track = useTrackEvent();
    const { pathname } = useLocation();
    const hasTrackedRef = useRef(false);
    useEffect(() => {
        if (hasTrackedRef.current)
            return;
        hasTrackedRef.current = true;
        track(AnalyticsArea.Error, "Page Not Found", { path: pathname });
    }, [track, pathname]);
    return React.createElement(Error, { errorCode: ERROR_CODES.PAGE_NOT_FOUND });
};
const Landing = React.lazy(() => import("@pages/landing"));
const EmbedSignup = React.lazy(() => import("@pages/embed-signup"));
const ReportType = React.lazy(() => import("@pages/report-type"));
const Checkout = React.lazy(() => import("@pages/checkout"));
const Results = React.lazy(() => import("@pages/results"));
const Login = React.lazy(() => import("@pages/login"));
const Demo = React.lazy(() => import("@pages/demo"));
const ForgotPassword = React.lazy(() => import("@pages/forgot-password"));
const ResetPassword = React.lazy(() => import("@pages/reset-password"));
const SSOLoginError = React.lazy(() => import("@pages/login-error"));
const InvalidToken = React.lazy(() => import("@pages/invalid-token"));
const AdminHome = React.lazy(() => import("@pages/admin-home"));
const Community = React.lazy(() => import("@pages/community"));
const Home = React.lazy(() => import("@pages/home"));
const NewReport = React.lazy(() => import("@pages/new-report"));
const Overview = React.lazy(() => import("@pages/overview"));
const ReportSettings = React.lazy(() => import("@pages/report-settings"));
const TermsConditionsAgreement = React.lazy(() => import("@pages/terms-conditions-agreement"));
const Error = React.lazy(() => import("@pages/error"));
const AppRoutes = () => {
    const { isAdmin } = useAuthContext();
    // Fire a page view on every route change across the whole app.
    usePageTracking();
    return (React.createElement(Suspense, { fallback: React.createElement(PeekScreenLoader, { layered: true }) },
        React.createElement(MobileGate, null),
        React.createElement(Routes, null,
            React.createElement(Route, { path: PATHS.LANDING, element: React.createElement(Landing, null) }),
            React.createElement(Route, { path: PATHS.EMBED_SIGNUP, element: React.createElement(EmbedSignup, null) }),
            React.createElement(Route, { path: PATHS.RESULTS, element: React.createElement(Results, null) }),
            React.createElement(Route, { element: React.createElement(AppLayout, null) },
                React.createElement(Route, { element: React.createElement(BoxLayout, null) },
                    React.createElement(Route, { path: PATHS.LOGIN, element: React.createElement(Login, null) }),
                    React.createElement(Route, { path: PATHS.DEMO, element: React.createElement(Demo, null) }),
                    React.createElement(Route, { path: PATHS.FORGOT_PASSWORD, element: React.createElement(ForgotPassword, null) }),
                    React.createElement(Route, { path: PATHS.RESET_PASSWORD, element: React.createElement(ResetPassword, null) }),
                    React.createElement(Route, { path: PATHS.LOGIN_ERROR, element: React.createElement(SSOLoginError, null) }),
                    React.createElement(Route, { path: PATHS.INVALID_TOKEN, element: React.createElement(InvalidToken, null) }),
                    React.createElement(Route, { path: PATHS.TERMS_CONDITIONS_AGREEMENT, element: React.createElement(TermsConditionsAgreement, null) }),
                    React.createElement(Route, { path: "*", element: React.createElement(NotFound, null) })),
                React.createElement(Route, { element: React.createElement(AuthLayout, null) },
                    React.createElement(Route, { path: PATHS.REPORT_TYPE, element: React.createElement(BillingRoute, null,
                            React.createElement(ReportType, null)) }),
                    React.createElement(Route, { path: PATHS.CHECKOUT, element: React.createElement(BillingRoute, null,
                            React.createElement(Checkout, null)) }),
                    React.createElement(Route, { path: PATHS.NEW_REPORT, element: React.createElement(BillingRoute, null,
                            React.createElement(NewReport, null)) }),
                    React.createElement(Route, { element: React.createElement(DataProviderLayout, null) },
                        isAdmin ? (React.createElement(Route, { path: PATHS.ADMIN_DASHBOARD, element: React.createElement(AdminHome, null) })) : (React.createElement(Route, { path: PATHS.HOME, element: React.createElement(Home, null) })),
                        React.createElement(Route, { path: PATHS.OVERVIEW, element: React.createElement(Overview, null) }),
                        React.createElement(Route, { path: PATHS.REPORT_SETTINGS, element: React.createElement(ReportSettings, null) })),
                    React.createElement(Route, { path: PATHS.COMMUNITY, element: React.createElement(Community, null) }))))));
};
export default AppRoutes;
